import { StateCreator } from "zustand";
import { supabase } from "../../../../lib/supabase";
import { Logger } from "../../../../lib/logger/Logger";
import { showNotification } from "./selectors";
import { PriceList, PriceListImport, ImportPriceListResult } from "../../types/price-list.types";
import { State } from "./types";
import { SupabaseWriteTableEnum } from "../../../../lib/supabase/supabaseTypes";

export interface PriceListsSlice {
	priceLists: PriceList[];
	priceListsLoading: boolean;
	priceListImports: PriceListImport[];
	priceListImportsLoading: boolean;

	loadPriceLists: () => Promise<void>;
	createPriceList: (name: string, regionId?: number) => Promise<void>;
	updatePriceList: (id: string, field: keyof PriceList, value: any) => Promise<void>;
	updatePriceListState: (id: string, field: keyof PriceList, value: any) => void;
	updatePriceListDb: (id: string, field: keyof PriceList, value: any) => Promise<void>;
	deletePriceList: (id: string) => Promise<void>;
	setDefaultPriceList: (id: string | null) => Promise<void>;
	loadPriceListImports: () => Promise<void>;
	importPriceListItems: (priceListImportId: string) => Promise<ImportPriceListResult | null>;
}

export const createPriceListsSlice: StateCreator<
	State & PriceListsSlice,
	[],
	[],
	PriceListsSlice
> = (set, get) => ({
	priceLists: [],
	priceListsLoading: false,
	priceListImports: [],
	priceListImportsLoading: false,

	loadPriceLists: async () => {
		const { organization } = get();
		if (!organization?.id) {
			Logger.warn("No organization ID found when loading price lists");
			return;
		}

		set({ priceListsLoading: true });

		try {
			const { data, error } = await supabase
				.from("price_lists")
				.select("*")
				.eq("organization_id", organization.id)
				.order("name", { ascending: true });

			if (error) {
				Logger.error("Error loading price lists: " + error.message);
				showNotification({
					message: "Fehler beim Laden der Preislisten",
					type: "error",
				});
				return;
			}

			set({ priceLists: data || [] });
		} catch (error) {
			Logger.error("Error loading price lists: " + String(error));
			showNotification({
				message: "Fehler beim Laden der Preislisten",
				type: "error",
			});
		} finally {
			set({ priceListsLoading: false });
		}
	},

	createPriceList: async (name: string, regionId?: number) => {
		const { organization, priceLists, activatedRegions } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return;
		}

		// Check activated regions
		if (!activatedRegions || activatedRegions.length === 0) {
			showNotification({
				message: "Keine Regionen aktiviert. Bitte aktivieren Sie zuerst eine Region.",
				type: "error",
			});
			return;
		}

		// Determine region ID
		let selectedRegionId: number;
		if (regionId !== undefined) {
			// Use provided region ID
			selectedRegionId = regionId;
		} else if (activatedRegions.length === 1) {
			// Auto-select if only one region
			selectedRegionId = activatedRegions[0].region;
		} else {
			// Multiple regions but none selected
			showNotification({
				message: "Bitte wählen Sie eine Region für die Preisliste aus.",
				type: "error",
			});
			return;
		}

		const newPriceList = {
			name,
			organization_id: organization.id,
			tp_value: 1.0,
			start_date: null,
			end_date: null,
			is_deactivated: false,
			region: selectedRegionId,
		};

		try {
			const { data, error } = await supabase
				.from("price_lists")
				.insert(newPriceList)
				.select()
				.single();

			if (error) {
				Logger.error("Error creating price list: " + error.message);
				showNotification({
					message: "Fehler beim Erstellen der Preisliste",
					type: "error",
				});
				return;
			}

			if (data) {
				set({ priceLists: [...priceLists, data] });

				// If it's the first price list, make it default
				if (priceLists.length === 0) {
					await get().setDefaultPriceList(data.id);
				}

				showNotification({
					message: "Preisliste erfolgreich erstellt",
					type: "success",
				});
			}
		} catch (error) {
			Logger.error("Error creating price list: " + String(error));
			showNotification({
				message: "Fehler beim Erstellen der Preisliste",
				type: "error",
			});
		}
	},

	updatePriceList: async (id: string, field: keyof PriceList, value: any) => {
		const { organization, priceLists } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when updating price list");
			return;
		}

		// Update local state immediately for responsiveness
		const updatedLists = priceLists.map(list =>
			list.id === id ? { ...list, [field]: value } : list
		);
		set({ priceLists: updatedLists });

		// Prepare update data
		const updateData: any = { [field]: value };

		try {
			const { error } = await supabase
				.from("price_lists")
				.update(updateData)
				.eq("id", id)
				.eq("organization_id", organization.id);

			if (error) {
				// Revert on error
				set({ priceLists });
				Logger.error("Error updating price list: " + error.message);
				showNotification({
					message: "Fehler beim Aktualisieren der Preisliste",
					type: "error",
				});
			}
		} catch (error) {
			// Revert on error
			set({ priceLists });
			Logger.error("Error updating price list: " + String(error));
			showNotification({
				message: "Fehler beim Aktualisieren der Preisliste",
				type: "error",
			});
		}
	},

	updatePriceListState: (id: string, field: keyof PriceList, value: any) => {
		const { priceLists } = get();
		const updatedLists = priceLists.map(list =>
			list.id === id ? { ...list, [field]: value } : list
		);
		set({ priceLists: updatedLists });
	},

	updatePriceListDb: async (id: string, field: keyof PriceList, value: any) => {
		const { organization } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when updating price list");
			return;
		}

		const updateData: any = { [field]: value };

		try {
			const { error } = await supabase
				.from("price_lists")
				.update(updateData)
				.eq("id", id)
				.eq("organization_id", organization.id);

			if (error) {
				Logger.error("Error updating price list in DB: " + error.message);
				showNotification({
					message: "Fehler beim Speichern der Preisliste",
					type: "error",
				});
			}
		} catch (error) {
			Logger.error("Error updating price list in DB: " + String(error));
			showNotification({
				message: "Fehler beim Speichern der Preisliste",
				type: "error",
			});
		}
	},

	deletePriceList: async (id: string) => {
		const { organization, priceLists } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when deleting price list");
			return;
		}

		// Check if it's the default price list
		const defaultPriceListId = (organization as any).default_price_list_id;
		if (id === defaultPriceListId) {
			showNotification({
				message: "Die Standard-Preisliste kann nicht gelöscht werden",
				type: "error",
			});
			return;
		}

		// Check if price list is in use by jobs
		const { count: jobsCount, error: jobsError } = await supabase
			.from("jobs")
			.select("*", { count: "exact", head: true })
			.eq("price_list_id", id);

		if (jobsError) {
			Logger.error("Error checking price list usage: " + jobsError.message);
			showNotification({
				message: "Fehler beim Überprüfen der Preislisten-Verwendung",
				type: "error",
			});
			return;
		}

		if (jobsCount && jobsCount > 0) {
			showNotification({
				message: "Diese Preisliste wird von Aufträgen verwendet und kann nicht gelöscht werden",
				type: "error",
			});
			return;
		}

		// Check if used as default by any client
		const { count: clientsCount, error: clientsError } = await supabase
			.from("clients")
			.select("*", { count: "exact", head: true })
			.eq("default_price_list_id", id);

		if (clientsError) {
			Logger.error("Error checking client price list usage: " + clientsError.message);
			showNotification({
				message: "Fehler beim Überprüfen der Preislisten-Verwendung",
				type: "error",
			});
			return;
		}

		if (clientsCount && clientsCount > 0) {
			showNotification({
				message: "Diese Preisliste wird von Kunden verwendet und kann nicht gelöscht werden",
				type: "error",
			});
			return;
		}

		try {
			const { error } = await supabase
				.from("price_lists")
				.delete()
				.eq("id", id)
				.eq("organization_id", organization.id);

			if (error) {
				Logger.error("Error deleting price list: " + error.message);
				showNotification({
					message: "Fehler beim Löschen der Preisliste",
					type: "error",
				});
				return;
			}

			// Update local state
			set({ priceLists: priceLists.filter(list => list.id !== id) });

			showNotification({
				message: "Preisliste erfolgreich gelöscht",
				type: "success",
			});
		} catch (error) {
			Logger.error("Error deleting price list: " + String(error));
			showNotification({
				message: "Fehler beim Löschen der Preisliste",
				type: "error",
			});
		}
	},

	setDefaultPriceList: async (id: string | null) => {
		const { organization } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when setting default price list");
			return;
		}

		try {
			const { error } = await supabase
				.from(SupabaseWriteTableEnum.ORGANIZATIONS)
				.update({ default_price_list_id: id })
				.eq("id", organization.id);

			if (error) {
				Logger.error("Error setting default price list: " + error.message);
				showNotification({
					message: "Fehler beim Setzen der Standard-Preisliste",
					type: "error",
				});
				return;
			}

			// Update local organization state
			set({
				organization: {
					...organization,
					...(id !== undefined && { default_price_list_id: id })
				} as any
			});

			showNotification({
				message: "Standard-Preisliste erfolgreich geändert",
				type: "success",
			});
		} catch (error) {
			Logger.error("Error setting default price list: " + String(error));
			showNotification({
				message: "Fehler beim Setzen der Standard-Preisliste",
				type: "error",
			});
		}
	},

	loadPriceListImports: async () => {
		set({ priceListImportsLoading: true });

		try {
			const { data, error } = await supabase
				.from("price_list_imports")
				.select("*")
				.order("name", { ascending: true });

			if (error) {
				Logger.error("Error loading price list imports: " + error.message);
				showNotification({
					message: "Fehler beim Laden der Preislisten-Importe",
					type: "error",
				});
				return;
			}

			set({ priceListImports: data || [] });
		} catch (error) {
			Logger.error("Error loading price list imports: " + String(error));
			showNotification({
				message: "Fehler beim Laden der Preislisten-Importe",
				type: "error",
			});
		} finally {
			set({ priceListImportsLoading: false });
		}
	},

	importPriceListItems: async (priceListImportId: string): Promise<ImportPriceListResult | null> => {
		const { organization, loadPriceLists } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return null;
		}

		try {
			const { data, error } = await supabase
				.rpc("import_price_list_items", {
					p_price_list_import_id: priceListImportId,
					p_organization_id: organization.id
				});

			if (error) {
				Logger.error("Error importing price list items: " + error.message);
				showNotification({
					message: "Fehler beim Importieren der Preislisten-Positionen",
					type: "error",
				});
				return null;
			}

			if (data && data.length > 0) {
				const result = data[0] as ImportPriceListResult;
				
				showNotification({
					message: `Preisliste erfolgreich importiert. ${result.imported_count} Positionen wurden hinzugefügt.`,
					type: "success",
				});

				// Reload price lists after successful import
				await loadPriceLists();

				return result;
			}

			return null;
		} catch (error) {
			Logger.error("Error importing price list items: " + String(error));
			showNotification({
				message: "Fehler beim Importieren der Preislisten-Positionen",
				type: "error",
			});
			return null;
		}
	},
});